import { useMemo } from 'react';
import { AgMultiAutocomplete } from 'src/components/ui';
import { cn } from 'src/components/ui/utils';
import convertToTitleCase from 'src/utils/convertToTitleCase';
import LabelTypeGroups from './LabelTypeGroups';
import { useLabelDefinitionsStore } from './labelDefinitions.store';

type TypeOption = { key: string; label: string; isGroup: boolean; groupTypes?: string[] };

// Display-only — AgCheckbox cannot be used inside AgMultiAutocomplete's renderOption because the parent ListBox already owns selection state.
function VisualCheckbox({ checked, indeterminate }: { checked: boolean; indeterminate?: boolean }) {
  return (
    <div
      className={cn(
        'w-4 h-4 rounded-xs border flex items-center justify-center shrink-0 transition-colors',
        checked || indeterminate ? 'bg-brand-6 border-brand-6' : 'border-neutral-5 bg-white'
      )}
    >
      {indeterminate && !checked ? (
        <span className="w-2 h-0.5 bg-white block" />
      ) : checked ? (
        <svg className="w-2.5 h-2.5 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={4}>
          <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
        </svg>
      ) : null}
    </div>
  );
}

export default function LabelTypeSelect() {
  const labelTypes = useLabelDefinitionsStore((state) => state.labelEditing?.new.labelTypes ?? []);
  const editLabel = useLabelDefinitionsStore((state) => state.editLabel);

  const labelTypeSet = useMemo(() => new Set(labelTypes), [labelTypes]);

  const typeOptions = useMemo<TypeOption[]>(() => {
    return Object.entries(LabelTypeGroups).flatMap(([group, types]) => [
      { key: group, label: convertToTitleCase(group), isGroup: true, groupTypes: types as string[] },
      ...types.map((type) => ({ key: type, label: convertToTitleCase(type), isGroup: false })),
    ]);
  }, []);

  const selectedOptions = useMemo(
    () => typeOptions.filter((opt) => !opt.isGroup && labelTypeSet.has(opt.key)),
    [typeOptions, labelTypeSet]
  );

  const handleChange = (newValue: TypeOption[]) => {
    const addedGroup = newValue.find((opt) => opt.isGroup);

    if (addedGroup) {
      const groupTypes = addedGroup.groupTypes!;
      const selectedInGroup = groupTypes.filter((t) => labelTypeSet.has(t));

      if (selectedInGroup.length === groupTypes.length) {
        editLabel(
          'labelTypes',
          labelTypes.filter((t) => !groupTypes.includes(t))
        );
      } else {
        const missing = groupTypes.filter((t) => !labelTypeSet.has(t));
        editLabel('labelTypes', [...labelTypes, ...missing]);
      }
    } else {
      editLabel(
        'labelTypes',
        newValue.map((opt) => opt.key)
      );
    }
  };

  return (
    <AgMultiAutocomplete
      label="Types"
      options={typeOptions}
      getOptionLabel={(opt) => opt.label}
      getOptionKey={(opt) => opt.key}
      value={selectedOptions}
      onChange={handleChange}
      limitTags={3}
      filterOptions={false}
      renderOption={(option, { isSelected }) => {
        if (option.isGroup) {
          const selectedCount = (option.groupTypes ?? []).filter((t) => labelTypeSet.has(t)).length;
          const total = option.groupTypes?.length ?? 0;
          return (
            <div className="flex items-center gap-2 w-full">
              <VisualCheckbox
                checked={selectedCount === total}
                indeterminate={selectedCount > 0 && selectedCount < total}
              />
              <span className="font-semibold text-14 text-neutral-text">{option.label}</span>
            </div>
          );
        }
        return (
          <div className="flex items-center gap-2 w-full pl-4">
            <VisualCheckbox checked={isSelected} />
            <span className="text-14 text-neutral-text">{option.label}</span>
          </div>
        );
      }}
    />
  );
}
